import React, { useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 3000 }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [onClose, duration]);

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500 text-white';
      case 'error':
        return 'bg-red-500 text-white';
      case 'info':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'info':
        return '🔵';
      default:
        return 'ℹ️';
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(message);
  };

  return (
    <div
      className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-md shadow-lg flex items-center gap-2 min-w-[250px] max-w-[400px] animate-slide-in ${getToastStyles()}`}
      onClick={onClose}
      style={{ cursor: 'pointer' }}
    >
      <span className="text-lg">{getIcon()}</span>
      <span className="flex-1 text-sm">{message}</span>

      {/* Copy button for errors */}
      {type === 'error' && (
        <button
          onClick={handleCopy}
          className="text-white hover:text-gray-200 text-sm p-1 hover:bg-red-600 rounded transition"
          title="Copy error message"
        >
          📋
        </button>
      )}

      <button
        onClick={onClose}
        className="text-white hover:text-gray-200 font-bold text-lg leading-none"
      >
        ×
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: Array<{ id: string; message: string; type: ToastType }>;
  removeToast: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, removeToast }) => {
  // Filter out info toasts (blue/progress toasts)
  const visibleToasts = toasts.filter(toast => toast.type !== 'info');

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      {visibleToasts.map((toast, index) => (
        <div key={toast.id} style={{ transform: `translateY(${index * 5}px)` }}>
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        </div>
      ))}
    </div>
  );
};

export default Toast;
